// src/component/auth/User_Registration.tsx - UPDATED with Mobile View
import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { ReactComponent as Logo } from "../../../assets/icons/svg/triva_logo.svg";
import { API_URLS } from "../../api/urls";
import { AuthStorage } from "./AuthStorage";
import { FirestoreService } from "../../../services/firestoreService";

interface UserRegistrationProps {
  onSubmit?: (firstName: string, lastName: string) => void;
  onBack?: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  userId?: number | null;
  accessToken?: string;
}

interface UpdateUserNameResponse {
  success: string;
  message: string;
  user_data?: any;
}

const UserRegistration: React.FC<UserRegistrationProps> = ({
  onSubmit,
  onBack,
  initialFirstName = "",
  initialLastName = "",
  userId: propUserId,
  accessToken: propAccessToken,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const callUpdateUserNameAPI = async (firstName: string, lastName: string) => {
    try {
      const userId = propUserId ?? AuthStorage.getUserId();
      const accessToken = propAccessToken || AuthStorage.getAccessToken() || "";

      console.log("Auth state — userId:", userId, "hasToken:", !!accessToken);

      if (!userId) {
        throw new Error("User not authenticated. Please login again.");
      }

      const urlParams = new URLSearchParams();
      urlParams.append('user_fname', firstName);
      urlParams.append('user_lname', lastName);

      const apiUrl = `${API_URLS.UPDATE_USER_NAME}${userId}/`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: urlParams.toString(),
      });

      let responseText = '';
      let result: UpdateUserNameResponse | null = null;

      try {
        responseText = await response.text();
        console.log("Raw response:", responseText);
        
        if (responseText) {
          result = JSON.parse(responseText);
        }
      } catch (parseError) {
        console.error("Failed to parse response:", parseError);
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication failed. Please login again.");
        }
        if (response.status === 500) {
          throw new Error(`Server error (500). Server response: ${responseText || 'No response body'}`);
        }
        throw new Error(`HTTP error! status: ${response.status}. Response: ${responseText}`);
      }

      if (result && result.success === "1") {
        console.log("✅ Backend: User name updated successfully");
        
        // ✅ NEW: Update Firestore as well
        try {
          console.log("=== Syncing to Firestore ===");
          await FirestoreService.updateUserName(
            userId.toString(),
            firstName,
            lastName
          );
          console.log("✅ Firestore: User name updated successfully");
        } catch (firestoreError) {
          console.error("⚠️ Firestore update failed:", firestoreError);
          // Don't throw - backend update succeeded
          console.warn("Continuing despite Firestore sync failure");
        }

        return result;
      } else {
        throw new Error(result?.message || "Failed to update user name - no success indicator");
      }

    } catch (error: any) {
      console.error("Update user name API error:", error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error("Network error: Unable to connect to the server. This could be due to CORS policy or server connectivity issues.");
      }
      
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setApiError("Please enter both first and last name");
      return;
    }

    setApiError("");
    setIsSubmitting(true);
    
    try {
      console.log("User registration:", { firstName, lastName });
      
      await callUpdateUserNameAPI(firstName.trim(), lastName.trim());
      
      if (onSubmit) {
        onSubmit(firstName.trim(), lastName.trim());
      }
      
    } catch (error: any) {
      console.error("Registration error:", error);
      setApiError(error.message || "Failed to update user name. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = firstName.trim() && lastName.trim();
  const isAuthenticated = !!(propUserId && propAccessToken) || AuthStorage.isAuthenticated();
  const isGoogleUser = initialFirstName || initialLastName;

  return (
    <div className="min-h-screen bg-white md:min-h-0 flex flex-col md:block">
      {/* Mobile: Logo and title */}
      <div className="md:hidden mb-4">
        <div className="mb-12">
          <Logo className="h-10" />
        </div>

        <div>
          <h1 className="text-[32px] font-bold text-[#282828] mb-2 leading-tight">
            {isGoogleUser ? "Confirm your details" : "What's your name?"}
          </h1>
          <p className="text-[17px] text-[#6D6D6D]">
            {isGoogleUser 
              ? "Please confirm or update your name details"
              : "Hey there! We can't wait to personalize your experience"
            }
          </p>
        </div>
      </div>

      {/* Desktop: Back Button */}
      {onBack && (
        <div className="hidden md:block mb-6">
          <button
            onClick={onBack}
            disabled={isSubmitting}
            className="flex items-center text-[#0DB5B5] hover:text-[#0A9999] transition-colors disabled:opacity-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      )}

      {/* Main Content Wrapper */}
      <div className="flex-1 md:flex-none flex flex-col md:block pb-[100px] md:pb-0">
        <div className="flex-1 md:flex-none bg-white md:rounded-[18px] rounded-none md:shadow-sm md:border-[0.5px] md:border-[#F2F2F2] p-0 md:p-8 flex flex-col">
          {/* Desktop: Title inside card */}
          <div className="hidden md:block text-center mb-6">
            <h1 className="text-[28px] 3xl:text-[32px] font-bold font-inter text-[#282828] mb-1">
              {isGoogleUser ? "Confirm your details" : "What's your name?"}
            </h1>
            <p className="text-[16px] 3xl:text-[18px] font-medium font-inter text-[#6D6D6D]">
              {isGoogleUser 
                ? "Please confirm or update your name details"
                : "Hey there! We can't wait to personalize your experience"
              }
            </p>
          </div>

          {/* Form Content */}
          <div className="space-y-5 flex-1 md:flex-none flex flex-col">
            {/* API Error Display */}
            {apiError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            )}

            {/* Authentication Warning */}
            {!isAuthenticated && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-700 text-sm">Please login first to update your name.</p>
              </div>
            )}

            {/* Google User Info */}
            {isGoogleUser && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-700 text-sm">
                  We've pre-filled your name from your Google account. You can modify it if needed.
                </p>
              </div>
            )}

            {/* First Name Input */}
            <div>
              <label className="block text-[15px] md:text-sm 3xl:text-[14px] text-[#202020] mb-3 md:mb-2 font-normal">
                Enter your first name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting || !isAuthenticated}
                className={`w-full px-3 py-2 border rounded-[12px]
                    focus:outline-none focus:ring-0 focus:border-[#ABECEB]
                   text-[16px] 3xl:text-[18px] transition-all ${
                     !isAuthenticated 
                       ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60' 
                       : 'border-[#ABECEB] disabled:opacity-50'
                   }`}
                placeholder="First name"
              />
            </div>

            {/* Last Name Input */}
            <div>
              <label className="block text-[15px] md:text-sm 3xl:text-[14px] text-[#202020] mb-3 md:mb-2 font-normal">
                Enter your last name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting || !isAuthenticated}
                className={`w-full px-3 py-2 border rounded-[12px]
                    focus:outline-none focus:ring-0 focus:border-[#ABECEB]
                   text-[16px] 3xl:text-[18px] transition-all ${
                     !isAuthenticated 
                       ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60' 
                       : 'border-[#ABECEB] disabled:opacity-50'
                   }`}
                placeholder="Last name"
              />
            </div>

            {/* Desktop: Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting || !isAuthenticated}
              className={`hidden md:block w-full text-[16px] 3xl:text-[18px] font-medium py-3 px-4 rounded-[8px] transition-all ${
                !isFormValid || isSubmitting || !isAuthenticated
                  ? 'bg-[#F1FBFB] text-[#00A9A9] opacity-50 cursor-not-allowed'
                  : 'bg-[#06B4B4] text-white hover:bg-[#059999]'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isGoogleUser ? 'Confirming Details...' : 'Updating Name...'}
                </div>
              ) : !isAuthenticated ? (
                'Please Login First'
              ) : (
                isGoogleUser ? 'Confirm Details' : 'Update Name'
              )}
            </button>

            {/* Mobile: Spacer to push button to bottom */}
            <div className="md:hidden flex-1"></div>
          </div>
        </div>

        {/* Mobile: Fixed bottom button */}
        <div className="md:hidden fixed -bottom-0.5 left-0 right-0 p-4" style={{ backdropFilter: "blur(10px)" }}>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting || !isAuthenticated}
            className={`w-full py-4 rounded-xl font-medium text-[16px] transition-colors duration-200 ${
              !isFormValid || isSubmitting || !isAuthenticated
                ? 'bg-[#F1FBFB] text-[#00A9A9] opacity-50 cursor-not-allowed'
                : 'bg-[#0DB5B5] text-white hover:bg-[#0A9999]'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {isGoogleUser ? 'Confirming Details...' : 'Updating Name...'}
              </div>
            ) : !isAuthenticated ? (
              'Please Login First'
            ) : (
              isGoogleUser ? 'Confirm Details' : 'Update Name'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserRegistration;